/**
 * Wake up at the moment a day-based answer changes, instead of asking all day.
 *
 * Some things here are not driven by a user action at all — they are driven by
 * the clock. "Yesterday's sales report is missing" becomes true at 02:00 IST
 * (the 26-hour grace in routes/dailyOperations.js), and "yesterday" itself
 * changes at IST midnight. Those are the only two instants when the answer can
 * turn over on its own.
 *
 * A 30-second poll was asking 2,880 times a day to find out about two moments.
 * Waiting for those two moments costs two queries and is exact, not up to 30
 * seconds late.
 *
 * The business runs on IST and the backend computes these dates in
 * Asia/Kolkata, so this deliberately does NOT use the viewer's local timezone —
 * an owner checking the books from another country must see the same lock as
 * the restaurant. IST has no daylight saving and a fixed +05:30 offset, which is
 * what makes the arithmetic below safe.
 */

const IST_OFFSET_MS = 5.5 * 60 * 60 * 1000;
const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * Milliseconds until the next of the given IST hours.
 *
 * @param {number[]} hours IST hours of the day, e.g. [0, 2]
 * @param {number}   [now] epoch ms, injectable for tests
 * @returns {number} always > 0
 */
export function msUntilNextIstHour(hours, now = Date.now()) {
  // The epoch starts at a UTC midnight and IST is a whole number of minutes
  // ahead, so shifting by the offset makes "time since IST midnight" a plain
  // remainder — no calendar handling, no DST.
  const sinceIstMidnight = (now + IST_OFFSET_MS) % DAY_MS;

  const targets = hours
    .map((hour) => hour * 60 * 60 * 1000)
    .sort((a, b) => a - b);

  for (const target of targets) {
    if (target > sinceIstMidnight) return target - sinceIstMidnight;
  }

  // All of today's boundaries have passed: the first one tomorrow.
  return DAY_MS - sinceIstMidnight + targets[0];
}

/**
 * Run `handler` at each of the given IST hours, for as long as the caller lives.
 *
 * Re-arms itself after every firing rather than using an interval, so it cannot
 * drift and so a machine that was asleep across a boundary simply fires late and
 * carries on at the right times.
 *
 * @param {number[]} hours   IST hours of the day, e.g. [0, 2]
 * @param {Function} handler
 * @returns {Function} cancel
 */
export function onIstHours(hours, handler) {
  let timerId = null;

  const arm = () => {
    timerId = setTimeout(() => {
      timerId = null;
      handler();
      arm();
    }, msUntilNextIstHour(hours));
  };

  arm();

  return () => {
    if (timerId) clearTimeout(timerId);
    timerId = null;
  };
}
